import React, { createContext, useState, useContext, useRef, useCallback } from 'react';
import { getAppMetadata } from '../services/api';
import storage from '../platform/storage';

const MetadataContext = createContext();

// Reference objects for external access
let metadataVersion = null;
let metadataRefreshFunction = null;
// 'en' until MetadataProvider has loaded the stored language
let currentLanguageValue = 'en';
let setCurrentLanguageFunction = null;

export const useMetadata = () => useContext(MetadataContext);

// Standalone functions for external access from non-react components (like from api.js)
export const getCurrentVersion = () => {
  return metadataVersion;
};

export const forceRefresh = async () => {
  if (metadataRefreshFunction) {
    return await metadataRefreshFunction();
  }
};

export const getCurrentLanguage = () => {
  return currentLanguageValue;
};

export const setCurrentLanguage = (language) => {
  if (setCurrentLanguageFunction) {
    setCurrentLanguageFunction(language);
    currentLanguageValue = language;
    // Update storage immediately
    storage.setItem('currentLanguage', language);
    return true;
  }
  return false;
};

export const MetadataProvider = ({ children }) => {
  const [metadata, setMetadata] = useState(null);
  // 'en' until the stored language is loaded; App.js holds rendering until
  // `hydrated` is true
  const [currentLanguage, setCurrentLanguageState] = useState('en');
  const [hydrated, setHydrated] = useState(false);
  // set once anything changes the language, so a late storage read cannot undo it
  const languageChangedRef = useRef(false);

  // the language the latest change asked for; cache reads for any other
  // language are dropped (see loadFromStorage)
  const latestLanguageRef = useRef('en');
  // bumped by every metadata load; a cache read that is no longer the latest
  // load (e.g. forceRefresh stored fresher data meanwhile) is dropped
  const metadataLoadSeqRef = useRef(0);
  // the language hydrate() already loaded the cache for
  const hydratedLanguageRef = useRef(null);

  const changeLanguage = useCallback((language) => {
    languageChangedRef.current = true;
    latestLanguageRef.current = language;
    setCurrentLanguageState(language);
  }, []);
  const [localMetadataVersion, setLocalMetadataVersion] = useState(null);
  
  // Use a ref to store the text cache to avoid re-renders
  const textCache = useRef({});
  
  // the refresh in flight and the latest language it was started for:
  // { language, promise }. Shared by every forceRefreshMetadata closure
  const inFlightRefreshRef = useRef(null);

  // Register the refresh function for external access.
  // Single-flight per language: while a refresh started for the latest
  // language is in flight, further calls get the same promise. A call after a
  // language switch starts a new request; the superseded one is cached only.
  const forceRefreshMetadata = useCallback(() => {
    const requestLanguage = latestLanguageRef.current;
    const inFlight = inFlightRefreshRef.current;
    if (inFlight && inFlight.language === requestLanguage) {
      return inFlight.promise;
    }

    const fetchMetadata = async () => {
      // full server code (en, de, …, zh-hans, zh-hant)
      const languageCode = currentLanguage;
    
      try {
        console.log("Fetching metadata from API, language:", languageCode);
      
        // Fetch from API
        const data = await getAppMetadata(languageCode);
      
        console.log("Metadata received for language:", languageCode);

        // Store the complete metadata in storage for future use, keyed by the
        // language of the response: this closure's language can be stale
        // (api.js calls setCurrentLanguage() and forceRefresh() in one tick,
        // before the refresh function for the new language is registered)
        const cachedMetadataKey = `appMetadata_${data.language_code || languageCode}`;
        storage.setItem(cachedMetadataKey, JSON.stringify(data));

        // A refresh answered for a language other than the latest one is
        // cached only: it must not replace the current language's data
        if (data.language_code
            && data.language_code !== latestLanguageRef.current) {
          console.log("Metadata for", data.language_code, "is not the current language; cached only");
          return data;
        }

        // supersede any cache read still in flight
        metadataLoadSeqRef.current += 1;
        setMetadata(data);
      
        // Clear the text cache when new metadata is loaded
        textCache.current = {};
      
        // Update the version state
        if (data.version) {
          setLocalMetadataVersion(data.version);
          metadataVersion = data.version; // Update external reference
        }
      
        return data;
      } catch (err) {
        console.error('Error loading app metadata:', err);
        throw err;
      }
    };

    const entry = { language: requestLanguage, promise: null };
    entry.promise = fetchMetadata().finally(() => {
      if (inFlightRefreshRef.current === entry) {
        inFlightRefreshRef.current = null;
      }
    });
    inFlightRefreshRef.current = entry;
    return entry.promise;
  }, [currentLanguage]);
  
  React.useEffect(() => {
    metadataRefreshFunction = forceRefreshMetadata;
    
    // Cleanup when unmounted
    return () => {
      if (metadataRefreshFunction === forceRefreshMetadata) {
        metadataRefreshFunction = null;
      }
    };
  }, [forceRefreshMetadata]);
  
  // Register the language setter function for external access
  React.useEffect(() => {
    setCurrentLanguageFunction = changeLanguage;
    currentLanguageValue = currentLanguage;
    
    // Cleanup when unmounted
    return () => {
      if (setCurrentLanguageFunction === changeLanguage) {
        setCurrentLanguageFunction = null;
      }
    };
  }, [changeLanguage, currentLanguage]);
  
  // Load the cached metadata for a language, and with it the version api.js
  // sends as x-metadata-version
  const loadFromStorage = useCallback(async (language) => {
    const languageCode = language;
    const cachedMetadataKey = `appMetadata_${languageCode}`;
    const loadSeq = ++metadataLoadSeqRef.current;
    const cachedMetadata = await storage.getItem(cachedMetadataKey);

    // Latest-language guard: drop a read that resolves after the language
    // changed or after a fresher load (forceRefresh) was applied
    if (loadSeq !== metadataLoadSeqRef.current
        || latestLanguageRef.current !== languageCode) {
      return null;
    }
    
    if (cachedMetadata) {
      try {
        const parsedMetadata = JSON.parse(cachedMetadata);
        
        // Verify the cached metadata has the correct language
        if (parsedMetadata.language_code === languageCode) {
          setMetadata(parsedMetadata);
          
          // Set the version from the metadata itself
          if (parsedMetadata.version) {
            setLocalMetadataVersion(parsedMetadata.version);
            metadataVersion = parsedMetadata.version; // Update external reference
          }
          
          // Clear the text cache when metadata changes
          textCache.current = {};
          
          return parsedMetadata;
        }
      } catch (err) {
        console.error("Error parsing cached metadata:", err);
      }
    }
    
    return null;
  }, []);
  
  // Function to force refresh metadata from API (moved above to fix use-before-define)
  
  // Function to get current metadata version
  const getCurrentVersion = useCallback(() => {
    return localMetadataVersion;
  }, [localMetadataVersion]);
  
  // Optimized function to get text from metadata
  const getText = useCallback((key) => {
    // Check if we have this key in the cache
    if (textCache.current[key] !== undefined) {
      return textCache.current[key];
    }
    
    if (!metadata) {
      return key;
    }
    
    // Split the key by dots to navigate the metadata object
    const parts = key.split('.');
    let value = metadata;
    
    for (const part of parts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        value = null;
        break;
      }
    }
    
    // Store the result in cache
    textCache.current[key] = value || key;
    return textCache.current[key];
  }, [metadata]);

  // Function to get theme palettes
  const getThemePalettes = useCallback(() => {
    return metadata?.theme_palettes || {};
  }, [metadata]);

  // Function to get fixed colors  
  const getFixedColors = useCallback(() => {
    return metadata?.fixed_colors || {};
  }, [metadata]);

  // Function to get available themes
  const getThemes = useCallback(() => {
    return metadata?.themes || [];
  }, [metadata]);
  
  // Load the stored language, its cached metadata and the metadata version on
  // mount. All of it is in place before `hydrated` flips: the first requests
  // fire in the commit that opens the gate and must carry x-metadata-version.
  React.useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const storedLanguage = await storage.getItem('currentLanguage');
      if (cancelled) return;
      if (storedLanguage && !languageChangedRef.current) {
        latestLanguageRef.current = storedLanguage;
        setCurrentLanguageState(storedLanguage);
      }
      const language = latestLanguageRef.current;
      await loadFromStorage(language);
      if (cancelled) return;
      hydratedLanguageRef.current = language;
      setHydrated(true);
    };

    hydrate();
    return () => { cancelled = true; };
  }, [loadFromStorage]);
  
  // Handle language changes after hydration
  React.useEffect(() => {
    // Writing before hydration would overwrite the stored language with the default
    if (!hydrated) return;
    // Store the current language in storage
    storage.setItem('currentLanguage', currentLanguage);
    // hydrate() already loaded the cache for the language it settled on
    const alreadyLoaded = hydratedLanguageRef.current === currentLanguage;
    hydratedLanguageRef.current = null;
    if (alreadyLoaded) return;
    // Clear text cache when language changes
    textCache.current = {};
    // Try to load metadata for the new language
    loadFromStorage(currentLanguage);
  }, [hydrated, currentLanguage, loadFromStorage]);
  
  return (
    <MetadataContext.Provider 
      value={{ 
        metadata,
        currentLanguage,
        hydrated,
        setCurrentLanguage: changeLanguage,
        getText,
        getCurrentVersion,
        forceRefresh: forceRefreshMetadata,
        getThemePalettes,
        getFixedColors,
        getThemes
      }}
    >
      {children}
    </MetadataContext.Provider>
  );
};
